import React, { useState, useEffect } from 'react';
import { ui } from '../../../config/uiSizes';
import { hasNoRenameableName } from '../../../utils/resourceTypes';

interface AddPrefixParametersProps {
  parameters?: Record<string, unknown>;
  onParametersChange?: (parameters: Record<string, unknown>) => void;
  /** Resource types (UI display names) this transformation is scoped to. */
  resourceTypes?: string[];
}

export const AddPrefixParameters: React.FC<AddPrefixParametersProps> = ({
  parameters,
  onParametersChange,
  resourceTypes = []
}) => {
  const [prefix, setPrefix] = useState<string>((parameters?.['prefix'] as string) || '');
  const [applyToProperty, setApplyToProperty] = useState<string>(
    (parameters?.['applyToProperty'] as string) || 'name'
  );

  // A prefix only has an effect on resource types that expose a renameable name. When the
  // transformation is scoped exclusively to types with no such name (e.g. DID pools, grammar
  // languages, email domains), the property picker is disabled so a no-op cannot be configured.
  const prefixHasNoEffect =
    resourceTypes.length > 0 && resourceTypes.every((type) => hasNoRenameableName(type));
  // Architect Flows are fully renameable — the published flow name is prefixed on import.
  const includesFlow = resourceTypes.includes('Architect Flow');

  useEffect(() => {
    if (onParametersChange) {
      onParametersChange({
        prefix,
        applyToProperty
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefix, applyToProperty]);

  return (
    <div className={`border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 ${ui.page.cardPadding}`}>
      <div className={`${ui.mb.title}`}>
        <h3 className={`${ui.text.sectionTitle} font-bold text-gray-900 dark:text-gray-100`}>Add Prefix</h3>
        <p className={`${ui.text.label} text-gray-600 dark:text-gray-400 mt-1`}>
          Add a prefix to resource names to distinguish migrated resources.
        </p>
      </div>

      <div className="space-y-4">
        {/* Prefix Input */}
        <div>
          <label className={`block ${ui.text.label} font-medium text-gray-700 dark:text-gray-300 mb-2`}>
            Prefix <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={prefix}
            onChange={(e) => setPrefix(e.target.value)}
            placeholder="MIGRATED_"
            className={`w-full ${ui.input.base} border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500`}
          />
          <p className={` ${ui.text.xs} text-gray-500 dark:text-gray-400 mt-1`}>
            Examples: "MIGRATED_", "PROD_", "NEW_"
          </p>
        </div>

        {/* Apply To Property */}
        <div>
          <label className={`block ${ui.text.label} font-medium text-gray-700 dark:text-gray-300 mb-2`}>
            Apply To Property
          </label>
          <input
            type="text"
            value={applyToProperty}
            onChange={(e) => setApplyToProperty(e.target.value)}
            placeholder="name"
            disabled={prefixHasNoEffect}
            className={`w-full ${ui.input.base} border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 ${prefixHasNoEffect ? 'opacity-50 cursor-not-allowed' : ''}`}
          />
          <p className={` ${ui.text.xs} text-gray-500 dark:text-gray-400 mt-1`}>
            Property name to modify (usually "name")
          </p>

          {prefixHasNoEffect && (
            <div className={`mt-2 p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md flex items-start ${ui.gap.sm}`}>
              <span className={`${ui.widthAndHeight.badgeDot} mt-1.5 rounded-full bg-amber-500 dark:bg-amber-400 shrink-0`} aria-hidden="true" />
              <span className={`${ui.text.xs} text-amber-700 dark:text-amber-300`}>
                The selected resource type(s) have no renameable name, so this prefix will not
                be applied. Choose a renameable type (e.g. Queues, Flows, Skills) for the prefix
                to take effect.
              </span>
            </div>
          )}

          {includesFlow && !prefixHasNoEffect && (
            <div className={`mt-2 p-2 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-md flex items-start ${ui.gap.sm}`}>
              <span className={`${ui.widthAndHeight.badgeDot} mt-1.5 rounded-full bg-emerald-500 dark:bg-emerald-400 shrink-0`} aria-hidden="true" />
              <span className={`${ui.text.xs} text-emerald-700 dark:text-emerald-300`}>
                Architect Flows are renameable — the published flow name will be prefixed on
                import.
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddPrefixParameters;
